import { Check, CheckCircle, Palette } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { Pressable } from "react-native";
import { TouchableOpacity } from "react-native-gesture-handler";
import {
  Circle,
  type GetThemeValueForKey,
  Image,
  Text,
  View,
  XStack,
  YStack,
} from "tamagui";
import { useUploadCover } from "../../../core/hooks/useUploadCover";
import { TagColorsAvailable } from "../../../core/tags/types";
import { DEFAULT_COVER_URL } from "../../../plans/types";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { EmojiPicker } from "../../../ui/EmojiPicker";
import { Input } from "../../../ui/InputNew";
import { Label } from "../../../ui/Label";
import { EditorForm } from "../../../ui/YooptaEditor";
import { BusinessPlanSettingsBox } from "../../plan/ui/BusinessPlanSettingsBox";
import type { BusinessTemplateModel } from "./BusinessTemplateModel";

export const BusinessAdminTemplateInfo = observer(
  ({ template }: { template: BusinessTemplateModel }) => {
    const [showEmojiSheet, setSetEmojiSheet] = useState(false);
    const [showColors, setShowColors] = React.useState(false);

    const uploadCoverButton = useUploadCover(
      (isUploading) => {
        console.info("Got is uploading", isUploading);
      },
      (coverUrl) => {
        return template.updateBusinessTemplatePlanInfo({
          coverUrl,
          backgroundColor: null,
        });
      },
      template.data.uid,
    );

    let coverUrl = template.data.planInfo.coverUrl;
    if (!coverUrl && !template.data.planInfo.backgroundColor) {
      coverUrl = DEFAULT_COVER_URL;
    }

    const icon = (
      <Text lineHeight={24} fontSize={24}>
        {template.data.planInfo.emoji}
      </Text>
    );

    return (
      <YStack gap="$space.medium">
        <Box gap="$small">
          <Label>Name</Label>
          <Input
            placeholder="Title"
            type="border"
            value={template.data.planInfo.title ?? ""}
            submitOnBlur
            onSubmitEditing={(event) => {
              template.updateBusinessTemplatePlanInfo({
                title: event.nativeEvent.text,
              });
            }}
            submitOnEnter
          />
        </Box>

        <Pressable onPress={() => setSetEmojiSheet(true)}>
          <Box>
            <XStack gap="$space.medium">
              <Label color={"$muted"}>Icon</Label>
              {icon}
            </XStack>
          </Box>
        </Pressable>

        <Box>
          <Label color={"$muted"}>Description</Label>
          <EditorForm
            id="description"
            value={template.data.planInfo.descriptionYooptaEditor}
            onSave={(data) =>
              template.updateBusinessTemplatePlanInfo({
                descriptionYooptaEditor: data,
              })
            }
          />
        </Box>

        <Box>
          <Label color={"$muted"}>Background theme</Label>
          {!!coverUrl && (
            <Image
              style={{ aspectRatio: 3 / 1 }}
              objectFit="cover"
              source={{ uri: coverUrl }}
            />
          )}
          {!!template.data.planInfo.backgroundColor && (
            <View
              style={{
                height: 100,
                backgroundColor: template.data.planInfo.backgroundColor,
              }}
            />
          )}

          {showColors && (
            <Box>
              <XStack
                width={"100%"}
                flexWrap="wrap"
                gap="$small"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Palette size={"$medium"} />
                {TagColorsAvailable.map((color) => {
                  const isCurrent =
                    color.color === template.data.planInfo.backgroundColor;

                  return (
                    <TouchableOpacity
                      key={color.color}
                      onPress={() => {
                        void template.updateBusinessTemplatePlanInfo({
                          backgroundColor: color.color,
                          coverUrl: null,
                        });
                      }}
                    >
                      <Circle
                        key={color.color}
                        style={{ backgroundColor: color.color }}
                        size="$medium"
                      >
                        {isCurrent && (
                          <CheckCircle
                            color={
                              color.textColor as GetThemeValueForKey<"color">
                            }
                          />
                        )}
                      </Circle>
                    </TouchableOpacity>
                  );
                })}
              </XStack>
            </Box>
          )}

          <XStack
            width={"100%"}
            gap={"$medium"}
            marginTop={"$small"}
            alignItems={"center"}
          >
            <View flex={1}>{uploadCoverButton}</View>
            <Label color={"$muted"}>or</Label>

            <View flex={1}>
              <Button
                icon={showColors ? Check : Palette}
                onPress={() => {
                  setShowColors(!showColors);
                }}
              >
                {showColors ? "Close" : "Color"}
              </Button>
            </View>
          </XStack>
        </Box>

        <BusinessPlanSettingsBox
          settings={template.data.settings}
          onChange={(updatedSettings) => {
            template.updateBusinessSettings(updatedSettings);
          }}
        />

        <EmojiPicker
          sheetId="emoji"
          open={showEmojiSheet}
          onOpenChange={(open: boolean) => setSetEmojiSheet(open)}
          onSelect={(emoji) => {
            template.updateBusinessTemplatePlanInfo({ emoji: emoji.emoji });
            setSetEmojiSheet(false);
          }}
        />
      </YStack>
    );
  },
);
